import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, ArrowLeft, Trash2, Copy, ExternalLink, Rocket, Save, Eye, EyeOff,
  CheckCircle, XCircle, Star, Info, ShoppingBag, CreditCard, Zap, Code2,
  GripVertical, Image, Tag, Users, Hash, Timer, Video, HelpCircle, ShieldCheck,
  MousePointerClick, TrendingUp, ImagePlus
} from 'lucide-react';
import { toast } from 'sonner';

const BULLET_ICONS = [
  { value: 'check', label: 'Centang', Icon: CheckCircle },
  { value: 'x', label: 'Silang', Icon: XCircle },
  { value: 'star', label: 'Bintang', Icon: Star },
  { value: 'info', label: 'Info', Icon: Info },
  { value: 'zap', label: 'Zap', Icon: Zap },
];

const DEFAULT_LP = {
  title: '',
  slug: '',
  product_id: '',
  headline: '',
  subheadline: '',
  hero_image_url: '',
  bullet_items: [],
  description_html: '',
  testimonials: [],
  form_title: 'Order Sekarang',
  show_phone: true,
  show_address: true,
  show_notes: false,
  primary_color: '#2D6CDF',
  button_text: 'Beli Sekarang',
  button_icon: 'ShoppingBag',
  footer_text: '',
  seo_title: '',
  seo_description: '',
  custom_html_blocks: [],
  enable_order_bump: false,
  bump_product_ids: [],
  enable_voucher: true,
  enable_affiliate: true,
  enable_quantity: false,
  bundle_id: '',
  is_active: true,
  video_url: '',
  video_title: '',
  faq_items: [],
  guarantee_text: '',
  guarantee_days: 0,
  enable_countdown: false,
  countdown_end: '',
  countdown_text: '',
  enable_floating_cta: true,
  enable_social_proof: false,
  social_proof_text: '',
  image_gallery: [],
};

export default function LandingPages() {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // list | editor
  const [form, setForm] = useState({ ...DEFAULT_LP });
  const [editId, setEditId] = useState(null);
  const [subdomain, setSubdomain] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [pagesRes, productsRes, settingsRes, bundlesRes, webinarsRes, eventsRes, bookingsRes] = await Promise.all([
        api.get('/tenant/landing-pages'),
        api.get('/tenant/products'),
        api.get('/tenant/settings'),
        api.get('/tenant/bundles').catch(() => ({ data: { bundles: [] } })),
        api.get('/tenant/webinars').catch(() => ({ data: { webinars: [] } })),
        api.get('/tenant/events').catch(() => ({ data: { events: [] } })),
        api.get('/tenant/booking-services').catch(() => ({ data: { services: [] } })),
      ]);
      setPages(pagesRes.data.landing_pages || []);
      const webinarItems = (webinarsRes.data.webinars || []).map(w => ({
        id: w.id, name: `[Webinar] ${w.title}`, price: w.price || 0, type: 'webinar'
      }));
      const eventItems = (eventsRes.data.events || []).map(e => ({
        id: e.id, name: `[Event] ${e.title}`, price: e.price || 0, type: 'event'
      }));
      const bookingItems = (bookingsRes.data.services || []).map(b => ({
        id: b.id, name: `[Booking] ${b.name}`, price: b.price || 0, type: 'booking'
      }));
      setProducts([...(productsRes.data.products || []), ...webinarItems, ...eventItems, ...bookingItems]);
      setBundles(bundlesRes.data.bundles || []);
      setSubdomain(settingsRes.data.tenant?.subdomain || '');
    } catch (e) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setForm({ ...DEFAULT_LP });
    setEditId(null);
    setView('editor');
  };

  const openEdit = (page) => {
    setForm({ ...DEFAULT_LP, ...page });
    setEditId(page.id);
    setView('editor');
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Judul wajib diisi');
    if (!form.product_id && !form.bundle_id) return toast.error('Pilih produk atau bundle');
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/tenant/landing-pages/${editId}`, form);
        toast.success('Landing page diperbarui');
      } else {
        await api.post('/tenant/landing-pages', form);
        toast.success('Landing page dibuat');
      }
      await fetchAll();
      setView('list');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus landing page ini?')) return;
    try {
      await api.delete(`/tenant/landing-pages/${id}`);
      toast.success('Dihapus');
      fetchAll();
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  const getPublicUrl = (page) => {
    const sub = page.subdomain || subdomain;
    return `https://${sub}.tokomoe.com/lp/${page.slug}`;
  };

  const getEmbedCode = (page) => {
    const url = getPublicUrl(page);
    return `<iframe src="${url}" width="100%" height="900" frameborder="0" style="border:none;max-width:500px;margin:0 auto;display:block;" allow="payment"></iframe>`;
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} disalin`);
  };

  // Bullet items
  const addBullet = () => setForm(f => ({ ...f, bullet_items: [...f.bullet_items, { icon: 'check', text: '' }] }));
  const updateBullet = (idx, field, val) => {
    const items = [...form.bullet_items];
    items[idx] = { ...items[idx], [field]: val };
    setForm(f => ({ ...f, bullet_items: items }));
  };
  const removeBullet = (idx) => setForm(f => ({ ...f, bullet_items: f.bullet_items.filter((_, i) => i !== idx) }));

  // Testimonials
  const addTestimonial = () => setForm(f => ({
    ...f, testimonials: [...f.testimonials, { name: '', role: '', avatar_url: '', rating: 5, text: '' }]
  }));
  const updateTestimonial = (idx, field, val) => {
    const items = [...form.testimonials];
    items[idx] = { ...items[idx], [field]: val };
    setForm(f => ({ ...f, testimonials: items }));
  };
  const removeTestimonial = (idx) => setForm(f => ({ ...f, testimonials: f.testimonials.filter((_, i) => i !== idx) }));

  // Custom HTML Blocks
  const addHtmlBlock = () => setForm(f => ({
    ...f, custom_html_blocks: [...(f.custom_html_blocks || []), { label: '', html: '', position: 'before_checkout' }]
  }));
  const updateHtmlBlock = (idx, field, val) => {
    const items = [...(form.custom_html_blocks || [])];
    items[idx] = { ...items[idx], [field]: val };
    setForm(f => ({ ...f, custom_html_blocks: items }));
  };
  const removeHtmlBlock = (idx) => setForm(f => ({ ...f, custom_html_blocks: (f.custom_html_blocks || []).filter((_, i) => i !== idx) }));

  // FAQ items
  const addFaq = () => setForm(f => ({ ...f, faq_items: [...(f.faq_items || []), { question: '', answer: '' }] }));
  const updateFaq = (idx, field, val) => {
    const items = [...(form.faq_items || [])];
    items[idx] = { ...items[idx], [field]: val };
    setForm(f => ({ ...f, faq_items: items }));
  };
  const removeFaq = (idx) => setForm(f => ({ ...f, faq_items: (f.faq_items || []).filter((_, i) => i !== idx) }));

  // Image gallery
  const addGalleryImage = () => setForm(f => ({ ...f, image_gallery: [...(f.image_gallery || []), ''] }));
  const updateGalleryImage = (idx, val) => {
    const imgs = [...(form.image_gallery || [])];
    imgs[idx] = val;
    setForm(f => ({ ...f, image_gallery: imgs }));
  };
  const removeGalleryImage = (idx) => setForm(f => ({ ...f, image_gallery: (f.image_gallery || []).filter((_, i) => i !== idx) }));

  const productName = (pid) => products.find(p => p.id === pid)?.name || '-';

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  // ═══════════ EDITOR VIEW ═══════════
  if (view === 'editor') {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('list')}><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="text-xl font-bold">{editId ? 'Edit Landing Page' : 'Buat Landing Page'}</h1>
          <div className="ml-auto flex gap-2">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-1" />{saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </div>

        {/* Basic Info */}
        <Card>
          <CardHeader><CardTitle className="text-base">Info Dasar</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Judul *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Promo Spesial" />
              </div>
              <div>
                <Label>Slug URL</Label>
                <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="otomatis dari judul" />
              </div>
            </div>
            <div>
              <Label>Produk {!form.bundle_id ? '*' : '(Opsional jika pakai bundle)'}</Label>
              <Select value={form.product_id || ''} onValueChange={v => setForm(f => ({ ...f, product_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Pilih produk..." /></SelectTrigger>
                <SelectContent>
                  {form.bundle_id && <SelectItem value="_none">Tanpa produk (pakai bundle)</SelectItem>}
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — Rp{(p.price || 0).toLocaleString('id-ID')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bundles.length > 0 && (
              <div>
                <Label>Bundle {!form.product_id ? '*' : '(Opsional)'}</Label>
                <Select value={form.bundle_id || ''} onValueChange={v => setForm(f => ({ ...f, bundle_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Tanpa bundle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Tanpa bundle</SelectItem>
                    {bundles.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name} — Rp{(b.final_price || 0).toLocaleString('id-ID')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Jika dipilih, bundle akan tampil sebagai produk utama di halaman checkout LP</p>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Label>Aktif</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </div>
          </CardContent>
        </Card>

        {/* Headline & Hero */}
        <Card>
          <CardHeader><CardTitle className="text-base">Headline & Hero Image</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Headline</Label>
              <Input value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} placeholder="Dapatkan Produk Terbaik Sekarang!" />
            </div>
            <div>
              <Label>Sub-headline</Label>
              <Input value={form.subheadline} onChange={e => setForm(f => ({ ...f, subheadline: e.target.value }))} placeholder="Penawaran terbatas..." />
            </div>
            <div>
              <Label>Hero Image URL</Label>
              <Input value={form.hero_image_url} onChange={e => setForm(f => ({ ...f, hero_image_url: e.target.value }))} placeholder="https://..." />
              {form.hero_image_url && (
                <img src={form.hero_image_url} alt="hero" className="mt-2 rounded-lg max-h-48 object-contain border" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bullet Items */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Daftar Keunggulan / Bullet</CardTitle>
              <Button size="sm" variant="outline" onClick={addBullet}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {form.bullet_items.length === 0 && <p className="text-sm text-muted-foreground">Belum ada bullet item.</p>}
            {form.bullet_items.map((b, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Select value={b.icon || 'check'} onValueChange={v => updateBullet(i, 'icon', v)}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BULLET_ICONS.map(ic => (
                      <SelectItem key={ic.value} value={ic.value}>
                        <span className="flex items-center gap-1"><ic.Icon className="h-3 w-3" />{ic.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input className="flex-1" value={b.text} onChange={e => updateBullet(i, 'text', e.target.value)} placeholder="Gratis ongkir..." />
                <Button size="icon" variant="ghost" onClick={() => removeBullet(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader><CardTitle className="text-base">Deskripsi / Copywriting</CardTitle></CardHeader>
          <CardContent>
            <Textarea rows={6} value={form.description_html} onChange={e => setForm(f => ({ ...f, description_html: e.target.value }))} placeholder="Tulis deskripsi produk yang menarik..." />
          </CardContent>
        </Card>

        {/* Video Embed */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Video className="h-4 w-4 text-red-500" />Video</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Judul Section Video</Label>
              <Input value={form.video_title || ''} onChange={e => setForm(f => ({ ...f, video_title: e.target.value }))} placeholder="Lihat Demo Produk" />
            </div>
            <div>
              <Label>URL Video (YouTube)</Label>
              <Input value={form.video_url || ''} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." />
              <p className="text-xs text-muted-foreground mt-1">Mendukung URL YouTube (watch, youtu.be, embed)</p>
            </div>
          </CardContent>
        </Card>

        {/* Image Gallery */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><ImagePlus className="h-4 w-4 text-sky-500" />Galeri Gambar</CardTitle>
              <Button size="sm" variant="outline" onClick={addGalleryImage}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(!form.image_gallery || form.image_gallery.length === 0) && <p className="text-sm text-muted-foreground">Belum ada gambar tambahan. Gambar ini akan tampil sebagai galeri produk.</p>}
            {(form.image_gallery || []).map((url, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input className="flex-1" value={url} onChange={e => updateGalleryImage(i, e.target.value)} placeholder="https://... (URL gambar)" />
                <Button size="icon" variant="ghost" onClick={() => removeGalleryImage(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                {url && <img src={url} alt={`gallery-${i}`} className="h-10 w-10 rounded object-cover border flex-shrink-0" />}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Testimonials */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Testimoni</CardTitle>
              <Button size="sm" variant="outline" onClick={addTestimonial}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.testimonials.length === 0 && <p className="text-sm text-muted-foreground">Belum ada testimoni.</p>}
            {form.testimonials.map((t, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex gap-2">
                  <Input className="flex-1" value={t.name} onChange={e => updateTestimonial(i, 'name', e.target.value)} placeholder="Nama" />
                  <Input className="w-32" value={t.role} onChange={e => updateTestimonial(i, 'role', e.target.value)} placeholder="Pekerjaan" />
                  <Select value={String(t.rating || 5)} onValueChange={v => updateTestimonial(i, 'rating', Number(v))}>
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[5,4,3,2,1].map(r => <SelectItem key={r} value={String(r)}>{'★'.repeat(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => removeTestimonial(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <Textarea rows={2} value={t.text} onChange={e => updateTestimonial(i, 'text', e.target.value)} placeholder="Isi testimoni..." />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Form Checkout Config */}
        <Card>
          <CardHeader><CardTitle className="text-base">Konfigurasi Form Checkout</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Judul Form</Label>
              <Input value={form.form_title} onChange={e => setForm(f => ({ ...f, form_title: e.target.value }))} />
            </div>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.show_phone} onCheckedChange={v => setForm(f => ({ ...f, show_phone: v }))} /> Tampilkan No HP
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.show_address} onCheckedChange={v => setForm(f => ({ ...f, show_address: v }))} /> Tampilkan Alamat
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.show_notes} onCheckedChange={v => setForm(f => ({ ...f, show_notes: v }))} /> Tampilkan Catatan
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Fitur Checkout Tambahan */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Tag className="h-4 w-4 text-emerald-500" />Fitur Checkout</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.enable_voucher ?? true} onCheckedChange={v => setForm(f => ({ ...f, enable_voucher: v }))} /> Kode Voucher
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.enable_affiliate ?? true} onCheckedChange={v => setForm(f => ({ ...f, enable_affiliate: v }))} /> Kode Referral / Affiliate
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.enable_quantity ?? false} onCheckedChange={v => setForm(f => ({ ...f, enable_quantity: v }))} /> Pilih Jumlah (Qty)
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Toggle fitur yang ditampilkan di halaman checkout landing page.</p>
          </CardContent>
        </Card>

        {/* Button & Style */}
        <Card>
          <CardHeader><CardTitle className="text-base">Tombol & Warna</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Teks Tombol</Label>
                <Input value={form.button_text} onChange={e => setForm(f => ({ ...f, button_text: e.target.value }))} />
              </div>
              <div>
                <Label>Ikon Tombol</Label>
                <Select value={form.button_icon} onValueChange={v => setForm(f => ({ ...f, button_icon: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ShoppingBag">🛍 Shopping Bag</SelectItem>
                    <SelectItem value="CreditCard">💳 Credit Card</SelectItem>
                    <SelectItem value="Zap">⚡ Zap</SelectItem>
                    <SelectItem value="Rocket">🚀 Rocket</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Warna Utama</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} className="h-9 w-12 rounded border cursor-pointer" />
                  <Input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} className="flex-1" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom HTML Blocks */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Code2 className="h-4 w-4" />Blok HTML / Script</CardTitle>
              <Button size="sm" variant="outline" onClick={addHtmlBlock}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {(!form.custom_html_blocks || form.custom_html_blocks.length === 0) && (
              <p className="text-sm text-muted-foreground">Belum ada blok HTML. Tambahkan untuk menyisipkan script tracking, pixel, widget, atau kode HTML apapun ke landing page.</p>
            )}
            {(form.custom_html_blocks || []).map((block, i) => (
              <div key={i} className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <div className="flex gap-2 items-center">
                  <Input
                    className="flex-1"
                    value={block.label}
                    onChange={e => updateHtmlBlock(i, 'label', e.target.value)}
                    placeholder="Label (misal: Facebook Pixel, GTM, Custom CSS...)"
                  />
                  <Select value={block.position || 'before_checkout'} onValueChange={v => updateHtmlBlock(i, 'position', v)}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="head">Di Head (meta/style)</SelectItem>
                      <SelectItem value="after_hero">Setelah Hero</SelectItem>
                      <SelectItem value="before_checkout">Sebelum Checkout</SelectItem>
                      <SelectItem value="after_checkout">Setelah Checkout</SelectItem>
                      <SelectItem value="footer">Di Footer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => removeHtmlBlock(i)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <Textarea
                  rows={5}
                  value={block.html}
                  onChange={e => updateHtmlBlock(i, 'html', e.target.value)}
                  placeholder={'Paste kode HTML/Script di sini...\n\nContoh:\n<script>...</script>\n<style>...</style>\n<div>...</div>'}
                  className="font-mono text-xs"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Order Bump */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" />Order Bump</CardTitle>
              <Switch checked={form.enable_order_bump || false} onCheckedChange={v => setForm(f => ({ ...f, enable_order_bump: v }))} />
            </div>
          </CardHeader>
          {form.enable_order_bump && (
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Tampilkan penawaran produk tambahan saat checkout. Pembeli bisa memilih untuk menambahkan atau tidak.</p>
              <div>
                <Label>Pilih Produk Bump (bisa lebih dari 1)</Label>
                <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                  {products.filter(p => p.id !== form.product_id).map(p => (
                    <label key={p.id} className="flex items-center gap-3 p-2 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={(form.bump_product_ids || []).includes(p.id)}
                        onChange={e => {
                          const ids = form.bump_product_ids || [];
                          setForm(f => ({
                            ...f,
                            bump_product_ids: e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id)
                          }));
                        }}
                        className="h-4 w-4 accent-amber-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground">Rp{(p.price || 0).toLocaleString('id-ID')}</p>
                      </div>
                    </label>
                  ))}
                </div>
                {(form.bump_product_ids || []).length > 0 && (
                  <p className="text-xs text-amber-600 mt-2">✓ {(form.bump_product_ids || []).length} produk bump dipilih</p>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* SEO */}
        <Card>
          <CardHeader><CardTitle className="text-base">SEO</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>SEO Title</Label>
              <Input value={form.seo_title} onChange={e => setForm(f => ({ ...f, seo_title: e.target.value }))} placeholder="Judul untuk mesin pencari" />
            </div>
            <div>
              <Label>SEO Description</Label>
              <Textarea rows={2} value={form.seo_description} onChange={e => setForm(f => ({ ...f, seo_description: e.target.value }))} placeholder="Deskripsi singkat..." />
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><HelpCircle className="h-4 w-4 text-blue-500" />FAQ / Pertanyaan Umum</CardTitle>
              <Button size="sm" variant="outline" onClick={addFaq}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {(!form.faq_items || form.faq_items.length === 0) && <p className="text-sm text-muted-foreground">Belum ada FAQ. Tambahkan pertanyaan & jawaban untuk mengurangi keraguan pembeli.</p>}
            {(form.faq_items || []).map((faq, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex gap-2">
                  <Input className="flex-1" value={faq.question} onChange={e => updateFaq(i, 'question', e.target.value)} placeholder="Pertanyaan..." />
                  <Button size="icon" variant="ghost" onClick={() => removeFaq(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <Textarea rows={2} value={faq.answer} onChange={e => updateFaq(i, 'answer', e.target.value)} placeholder="Jawaban..." />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Countdown Timer */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Timer className="h-4 w-4 text-red-500" />Countdown Timer</CardTitle>
              <Switch checked={form.enable_countdown || false} onCheckedChange={v => setForm(f => ({ ...f, enable_countdown: v }))} />
            </div>
          </CardHeader>
          {form.enable_countdown && (
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Tampilkan timer hitung mundur untuk menciptakan urgensi.</p>
              <div>
                <Label>Teks Countdown</Label>
                <Input value={form.countdown_text || ''} onChange={e => setForm(f => ({ ...f, countdown_text: e.target.value }))} placeholder="Penawaran berakhir dalam:" />
              </div>
              <div>
                <Label>Berakhir Pada</Label>
                <Input type="datetime-local" value={form.countdown_end || ''} onChange={e => setForm(f => ({ ...f, countdown_end: e.target.value }))} />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Guarantee */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-green-500" />Garansi / Jaminan</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Jumlah Hari Garansi</Label>
              <Input type="number" min={0} value={form.guarantee_days || 0} onChange={e => setForm(f => ({ ...f, guarantee_days: parseInt(e.target.value) || 0 }))} placeholder="30" />
              <p className="text-xs text-muted-foreground mt-1">Isi 0 jika tidak ada garansi. Contoh: 30 = Garansi 30 Hari Uang Kembali.</p>
            </div>
            <div>
              <Label>Teks Garansi</Label>
              <Textarea rows={2} value={form.guarantee_text || ''} onChange={e => setForm(f => ({ ...f, guarantee_text: e.target.value }))} placeholder="Jika tidak puas, uang Anda akan dikembalikan 100% tanpa pertanyaan!" />
            </div>
          </CardContent>
        </Card>

        {/* Social Proof */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-orange-500" />Social Proof</CardTitle>
              <Switch checked={form.enable_social_proof || false} onCheckedChange={v => setForm(f => ({ ...f, enable_social_proof: v }))} />
            </div>
          </CardHeader>
          {form.enable_social_proof && (
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Tampilkan jumlah pembeli atau teks social proof untuk meningkatkan kepercayaan.</p>
              <div>
                <Label>Teks Social Proof</Label>
                <Input value={form.social_proof_text || ''} onChange={e => setForm(f => ({ ...f, social_proof_text: e.target.value }))} placeholder="500+ orang sudah membeli produk ini!" />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Floating CTA */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-indigo-500" />Floating CTA Button</CardTitle>
              <Switch checked={form.enable_floating_cta ?? true} onCheckedChange={v => setForm(f => ({ ...f, enable_floating_cta: v }))} />
            </div>
          </CardHeader>
          {(form.enable_floating_cta ?? true) && (
            <CardContent>
              <p className="text-sm text-muted-foreground">Tombol "Beli Sekarang" akan selalu terlihat di bawah layar saat pengunjung scroll ke bawah.</p>
            </CardContent>
          )}
        </Card>

        {/* Footer */}
        <Card>
          <CardHeader><CardTitle className="text-base">Footer</CardTitle></CardHeader>
          <CardContent>
            <Input value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))} placeholder="© 2026 Toko Saya" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ═══════════ LIST VIEW ═══════════
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Landing Page</h1>
          <p className="text-muted-foreground">Buat halaman checkout khusus per produk, bisa di-embed ke website luar.</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Buat Landing Page</Button>
      </div>

      {pages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Rocket className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="font-semibold">Belum ada landing page</p>
            <p className="text-sm text-muted-foreground mb-4">Buat landing page checkout pertamamu!</p>
            <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Buat Sekarang</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {pages.map(page => (
            <Card key={page.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                    {page.hero_image_url ? (
                      <img src={page.hero_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{page.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${page.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {page.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      Produk: {productName(page.product_id)} &middot; /{page.slug}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => copyText(getPublicUrl(page), 'Link')}>
                      <Copy className="h-3 w-3 mr-1" />Link
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyText(getEmbedCode(page), 'Embed code')}>
                      <Code2 className="h-3 w-3 mr-1" />Embed
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(getPublicUrl(page), '_blank')}>
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(page)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(page.id)}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
